import { supabase } from '../lib/supabase';

export const adminService = {
  // ============================================================================
  // DASHBOARD METRICS
  // ============================================================================

  async getDashboardMetrics() {
    try {
      // Get total registrations count
      const { count: totalCount, error: totalError } = await supabase?.from('registrations')?.select('*', { count: 'exact', head: true });

      if (totalError) throw totalError;

      // Get pending reviews count
      const { count: pendingCount, error: pendingError } = await supabase?.from('registrations')?.select('*', { count: 'exact', head: true })?.eq('status', 'submitted');

      if (pendingError) throw pendingError;

      // Get approved count
      const { count: approvedCount, error: approvedError } = await supabase?.from('registrations')?.select('*', { count: 'exact', head: true })?.eq('status', 'approved');

      if (approvedError) throw approvedError;

      // Get active cities count
      const { data: citySettings, error: cityError } = await supabase?.from('city_registration_settings')?.select('city')?.eq('is_open', true);

      if (cityError) throw cityError;

      return {
        totalRegistrations: totalCount || 0,
        pendingReviews: pendingCount || 0,
        approvedTeams: approvedCount || 0,
        activeCities: citySettings?.length || 0
      };
    } catch (error) {
      console.error('Error fetching dashboard metrics:', error);
      throw error;
    }
  },

  // ============================================================================
  // CHART DATA
  // ============================================================================

  async getRegistrationsByCity() {
    try {
      const { data, error } = await supabase?.from('registrations')?.select('city, age_category');

      if (error) throw error;

      // Group by city and age category
      const cityData = {};
      data?.forEach(reg => {
        if (!cityData?.[reg?.city]) {
          cityData[reg.city] = {
            city: reg?.city,
            boysU14: 0,
            girlsU15: 0,
            boysU16: 0
          };
        }

        switch (reg?.age_category) {
          case 'boys_u14':
            cityData[reg.city].boysU14++;
            break;
          case 'girls_u15':
            cityData[reg.city].girlsU15++;
            break;
          case 'boys_u16':
            cityData[reg.city].boysU16++;
            break;
        }
      });

      return Object.values(cityData);
    } catch (error) {
      console.error('Error fetching city data:', error);
      throw error;
    }
  },

  // ============================================================================
  // REGISTRATIONS TABLE
  // ============================================================================

  async getFilteredRegistrations(filters, pagination = { page: 1, pageSize: 10 }) {
    try {
      const { page = 1, pageSize = 10 } = pagination;
      const offset = (page - 1) * pageSize;

      // If filtering by school names or schoolId, first get the school IDs
      let schoolIds = null;
      if (filters?.schoolId) {
        // Direct school ID filter (for player_cards)
        schoolIds = [filters.schoolId];
      } else if (filters?.schoolNames && Array.isArray(filters?.schoolNames) && filters?.schoolNames.length > 0) {
        const { data: schoolsData, error: schoolsError } = await supabase
          ?.from('schools')
          ?.select('id')
          ?.in('name', filters?.schoolNames);
        
        if (schoolsError) throw schoolsError;
        schoolIds = schoolsData?.map(s => s.id) || [];
        // If no schools found, return empty result
        if (schoolIds.length === 0) {
          return {
            data: [],
            total: 0,
            page,
            pageSize,
            totalPages: 0
          };
        }
      }

      // First, get the total count for pagination
      // Use the same query structure as data query to ensure accurate count
      let countQuery = supabase?.from('registrations')?.select(`
        id,
        coaches!inner(id)
      `, { count: 'exact', head: true });

      // Apply same filters to count query
      if (filters?.city && filters?.city !== 'all') {
        countQuery = countQuery?.eq('city', filters?.city);
      }
      if (filters?.status && filters?.status !== 'all') {
        countQuery = countQuery?.eq('status', filters?.status);
      }
      if (filters?.ageGroup && filters?.ageGroup !== 'all') {
        const ageCategoryMap = {
          'boysU14': 'boys_u14',
          'girlsU15': 'girls_u15',
          'boysU16': 'boys_u16'
        };
        const dbAgeCategory = ageCategoryMap[filters?.ageGroup] || filters?.ageGroup;
        countQuery = countQuery?.eq('age_category', dbAgeCategory);
      }
      // Gender filter (derived from age_category)
      if (filters?.gender && filters?.gender !== 'all') {
        if (filters?.gender === 'male') {
          countQuery = countQuery?.in('age_category', ['boys_u14', 'boys_u16']);
        } else if (filters?.gender === 'female') {
          countQuery = countQuery?.eq('age_category', 'girls_u15');
        }
      }
      // Handle school filter - support both single string (backward compatibility) and array
      if (schoolIds && schoolIds.length > 0) {
        // Multiple schools selected - filter by school_id
        countQuery = countQuery?.in('school_id', schoolIds);
      } else if (filters?.schoolName && filters?.schoolName !== 'all') {
        // Single school (backward compatibility) - get school ID first
        if (typeof filters?.schoolName === 'string' && filters?.schoolName.trim() !== '') {
          const { data: schoolData, error: schoolError } = await supabase
            ?.from('schools')
            ?.select('id')
            ?.ilike('name', `%${filters?.schoolName}%`)
            ?.limit(1)
            ?.single();
          
          if (!schoolError && schoolData?.id) {
            countQuery = countQuery?.eq('school_id', schoolData.id);
          } else {
            // If school not found, return empty result
            return {
              data: [],
              total: 0,
              page,
              pageSize,
              totalPages: 0
            };
          }
        }
      }
      if (filters?.dateFrom) {
        countQuery = countQuery?.gte('created_at', filters?.dateFrom);
      }
      if (filters?.dateTo) {
        countQuery = countQuery?.lte('created_at', filters?.dateTo + 'T23:59:59');
      }

      const { count, error: countError } = await countQuery;
      if (countError) throw countError;

      // Now get the paginated data
      let query = supabase?.from('registrations')?.select(`
          id,
          registration_number,
          city,
          age_category,
          poc_name,
          status,
          submitted_at,
          created_at,
          updated_at,
          schools (
            name
          ),
          coaches!inner (
            name,
            is_primary
          ),
          user_profiles (
            full_name
          )
        `)?.order('created_at', { ascending: false })
        ?.range(offset, offset + pageSize - 1);

      // Apply filters
      if (filters?.city && filters?.city !== 'all') {
        query = query?.eq('city', filters?.city);
      }
      if (filters?.status && filters?.status !== 'all') {
        query = query?.eq('status', filters?.status);
      }
      if (filters?.ageGroup && filters?.ageGroup !== 'all') {
        // Map filter values to database enum format
        const ageCategoryMap = {
          'boysU14': 'boys_u14',
          'girlsU15': 'girls_u15',
          'boysU16': 'boys_u16'
        };
        const dbAgeCategory = ageCategoryMap[filters?.ageGroup] || filters?.ageGroup;
        query = query?.eq('age_category', dbAgeCategory);
      }
      // Gender filter (derived from age_category)
      if (filters?.gender && filters?.gender !== 'all') {
        if (filters?.gender === 'male') {
          query = query?.in('age_category', ['boys_u14', 'boys_u16']);
        } else if (filters?.gender === 'female') {
          query = query?.eq('age_category', 'girls_u15');
        }
      }
      // Handle school filter - support both single string (backward compatibility) and array
      if (schoolIds && schoolIds.length > 0) {
        // Multiple schools selected - filter by school_id
        query = query?.in('school_id', schoolIds);
      } else if (filters?.schoolName && filters?.schoolName !== 'all') {
        // Single school (backward compatibility) - use the same school ID from count query
        if (typeof filters?.schoolName === 'string' && filters?.schoolName.trim() !== '') {
          const { data: schoolData, error: schoolError } = await supabase
            ?.from('schools')
            ?.select('id')
            ?.ilike('name', `%${filters?.schoolName}%`)
            ?.limit(1)
            ?.single();
          
          if (!schoolError && schoolData?.id) {
            query = query?.eq('school_id', schoolData.id);
          }
        }
      }
      if (filters?.dateFrom) {
        query = query?.gte('created_at', filters?.dateFrom);
      }
      if (filters?.dateTo) {
        query = query?.lte('created_at', filters?.dateTo + 'T23:59:59');
      }

      const { data, error } = await query;

      if (error) throw error;

      // Transform data to match expected format
      const transformedData = data?.map(reg => ({
        id: reg?.registration_number || reg?.id,
        dbId: reg?.id, // Store the actual database UUID for operations
        schoolName: reg?.schools?.name || 'Unknown School',
        city: reg?.city,
        ageCategory: this.formatAgeCategory(reg?.age_category),
        coachName: reg?.coaches?.find(c => c?.is_primary)?.name || reg?.coaches?.[0]?.name || reg?.poc_name,
        submittedDate: reg?.submitted_at ? new Date(reg.submitted_at)?.toLocaleDateString('en-GB') : new Date(reg.created_at)?.toLocaleDateString('en-GB'),
        status: reg?.status,
        reviewer: reg?.user_profiles?.full_name || null
      })) || [];

      return {
        data: transformedData,
        total: count || 0,
        page,
        pageSize,
        totalPages: Math.ceil((count || 0) / pageSize)
      };
    } catch (error) {
      console.error('Error fetching filtered registrations:', error);
      throw error;
    }
  },

  // ============================================================================
  // RECENT ACTIVITIES
  // ============================================================================

  async getRecentActivities(limit = 10) {
    try {
      // Get recent registration submissions
      const { data: recentRegs, error: regsError } = await supabase?.from('registrations')?.select('id, registration_number, status, submitted_at, created_at, schools(name)')?.order('updated_at', { ascending: false })?.limit(limit);

      if (regsError) throw regsError;

      // Get recent communications from campaigns
      const { data: recentComms, error: commsError } = await supabase
        ?.from('communication_campaigns')
        ?.select('id, communication_campaign_type, created_at, status, name')
        ?.order('created_at', { ascending: false })
        ?.limit(limit);

      if (commsError) throw commsError;

      // Combine and sort activities
      const activities = [];

      recentRegs?.forEach(reg => {
        let activityType, title, description, status;

        if (reg?.status === 'submitted' && reg?.submitted_at) {
          activityType = 'registration';
          title = 'New Registration Submitted';
          description = `${reg?.schools?.name || 'Unknown School'} submitted team registration`;
          status = 'submitted';
        } else if (reg?.status === 'approved') {
          activityType = 'approval';
          title = 'Registration Approved';
          description = `${reg?.schools?.name || 'Unknown School'} registration approved`;
          status = 'approved';
        } else if (reg?.status === 'rejected') {
          activityType = 'rejection';
          title = 'Registration Rejected';
          description = `${reg?.schools?.name || 'Unknown School'} registration rejected`;
          status = 'rejected';
        }

        if (title) {
          activities?.push({
            id: reg?.id,
            type: activityType,
            title,
            description,
            timestamp: this.formatTimestamp(reg?.submitted_at || reg?.created_at),
            status
          });
        }
      });

      recentComms?.forEach(comm => {
        activities?.push({
          id: comm?.id,
          type: 'communication',
          title: comm?.name || 'Communication Campaign',
          description: `Campaign: ${comm?.communication_campaign_type || 'N/A'}`,
          timestamp: this.formatTimestamp(comm?.created_at),
          status: comm?.status
        });
      });

      // Sort by timestamp and return top items
      return activities?.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))?.slice(0, limit);
    } catch (error) {
      console.error('Error fetching recent activities:', error);
      throw error;
    }
  },

  // ============================================================================
  // CITY SETTINGS
  // ============================================================================

  async getCitySettings() {
    try {
      const { data, error } = await supabase?.from('city_registration_settings')?.select('*')?.order('city');

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error fetching city settings:', error);
      throw error;
    }
  },

  async updateCitySettings(city, isOpen, closedMessage = null) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data, error } = await supabase?.from('city_registration_settings')?.update({
          is_open: isOpen,
          closed_message: closedMessage,
          updated_by: user?.id,
          updated_at: new Date()?.toISOString()
        })?.eq('city', city)?.select()?.single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error updating city settings:', error);
      throw error;
    }
  },

  // ============================================================================
  // HELPER FUNCTIONS
  // ============================================================================

  formatAgeCategory(category) {
    const map = {
      'boys_u14': 'Boys U14',
      'girls_u15': 'Girls U15',
      'boys_u16': 'Boys U16'
    };
    return map?.[category] || category;
  },

  formatTimestamp(timestamp) {
    if (!timestamp) return 'Unknown';
    
    const date = new Date(timestamp);
    const now = new Date();
    const diffInMs = now - date;
    const diffInHours = Math.floor(diffInMs / (1000 * 60 * 60));
    const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));

    if (diffInHours < 1) return 'Just now';
    if (diffInHours < 24) return `${diffInHours} hour${diffInHours > 1 ? 's' : ''} ago`;
    if (diffInDays < 7) return `${diffInDays} day${diffInDays > 1 ? 's' : ''} ago`;
    return date?.toLocaleDateString('en-GB');
  }
};